'use client';

import { usePathname } from 'next/navigation';
import Script from 'next/script';
import { useEffect, useRef, useState } from 'react';

// A simple browser fingerprint
function generateFingerprint() {
  if (typeof window === 'undefined') return 'server';
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.textBaseline = 'top';
    ctx.font = "14px 'Arial'";
    ctx.fillStyle = '#f60';
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = '#069';
    ctx.fillText('zappingai verification', 2, 15);
    ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
    ctx.fillText('zappingai verification', 4, 17);
  }
  const canvasData = canvas.toDataURL();

  const components = [
    navigator.userAgent,
    navigator.language,
    screen.colorDepth,
    `${screen.width}x${screen.height}`,
    new Date().getTimezoneOffset(),
    Intl.DateTimeFormat().resolvedOptions().timeZone,
    canvasData,
  ];

  let hash = 0;
  const str = components.join('|');
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }
  return Math.abs(hash).toString(16);
}

// Function to set a cookie dynamically on the client
function setCookie(name: string, value: string, maxAgeSeconds: number) {
  document.cookie = `${name}=${encodeURIComponent(value)}; max-age=${maxAgeSeconds}; path=/; SameSite=Lax; Secure`;
}

export function SecurityProvider({ children }: { children: React.ReactNode }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const pathname = usePathname();
  const [shouldRenderScript, setShouldRenderScript] = useState(false);

  const isProtectedArea = pathname?.includes('/admin') || pathname?.includes('/user');
  const skipTurnstile = isProtectedArea;

  useEffect(() => {
    // Global delay for script rendering to clear the main thread
    const renderDelay = setTimeout(() => {
      setShouldRenderScript(true);
    }, 5000);

    return () => clearTimeout(renderDelay);
  }, []);

  useEffect(() => {
    // 1. Generate and set fingerprint cookie (valid for 1 day)
    // Defer to prevent LHR/Scheduler violations during boot
    const deferFingerprint = () => {
      const fp = generateFingerprint();
      setCookie('_z_v', fp, 86400);
    };

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(() => setTimeout(deferFingerprint, 1000));
    } else {
      setTimeout(deferFingerprint, 3000); // Increased delay
    }

    const initTurnstile = () => {
      // @ts-ignore
      if (window.turnstile && containerRef.current && !widgetIdRef.current) {
        if (skipTurnstile) return;

        const siteKey =
          process.env.NEXT_PUBLIC_CLOUDFLARE_TURNSTILE_SITE_KEY ||
          process.env.CLOUDFLARE_TURNSTILE_SITE_KEY;
        if (!siteKey) {
          console.warn(
            'SecurityProvider: Missing CLOUDFLARE_TURNSTILE_SITE_KEY. Turnstile not disabled.'
          );
          return;
        }

        try {
          // @ts-ignore
          widgetIdRef.current = window.turnstile.render(containerRef.current, {
            sitekey: siteKey,
            theme: 'dark',
            size: 'flexible',
            appearance: 'always',
            callback: (token: string) => {
              // Store token in an accessible cookie (valid for 4 minutes, expires in 5s upstream)
              setCookie('cf_turnstile_token', token, 240);
            },
            'error-callback': () => {
              console.error('Turnstile verification failed');
            },
            'expired-callback': () => {
              // @ts-ignore
              if (widgetIdRef.current) window.turnstile.reset(widgetIdRef.current);
            },
          });
        } catch (e) {
          console.error('Failed to render turnstile:', e);
        }
      }
    };

    if (typeof window !== 'undefined') {
      // @ts-ignore
      if (window.turnstile) {
        initTurnstile();
      } else {
        // @ts-ignore
        window.onloadTurnstileCallback = initTurnstile;
      }
    }

    return () => {
      // @ts-ignore
      if (widgetIdRef.current && window.turnstile) {
        // @ts-ignore
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [pathname, skipTurnstile]);

  return (
    <>
      {!skipTurnstile && shouldRenderScript && (
        <Script
          src="https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onloadTurnstileCallback"
          strategy="lazyOnload"
        />
      )}
      <div
        ref={containerRef}
        style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', zIndex: -1 }}
      />
      {children}
    </>
  );
}
